import { z } from 'zod';
import { defineStateMachine } from '../common/state-machine.js';
/**
 * `PaymentIntent` (módulo `payments`) — `03-maquinas-de-estado.md` §1.
 * Nota: la API pública usa estos mismos nombres (`AWAITING_SCA`, `CANCELLED`); los alias del índice
 * de API (`REQUIRES_SCA`, `CANCELED`) quedan reemplazados por los de la máquina normativa.
 */
export const PaymentIntentMachine = defineStateMachine({
    name: 'PaymentIntent',
    states: [
        'CREATED',
        'AWAITING_SCA',
        'VALIDATING',
        'CREDIT_AUTHORIZED',
        'FUNDING',
        'FUNDED',
        'SENT_TO_BREB',
        'PENDING_CONFIRMATION',
        'COMPENSATING',
        'COMPLETED',
        'FAILED',
        'CANCELLED',
        'EXPIRED',
    ],
    initial: ['CREATED'],
    terminal: ['COMPLETED', 'FAILED', 'CANCELLED', 'EXPIRED'],
    invalidTransitionCode: 'PZ-PI-409',
    transitions: {
        CREATED: ['AWAITING_SCA', 'CANCELLED', 'EXPIRED'],
        // AWAITING_SCA → AWAITING_SCA: SCA inválida con intentos < 3.
        AWAITING_SCA: ['VALIDATING', 'AWAITING_SCA', 'FAILED', 'CANCELLED'],
        VALIDATING: ['CREDIT_AUTHORIZED', 'FAILED'],
        // LINKED_ACCOUNT_JIT → FUNDING; SETTLEMENT_ACCOUNT → SENT_TO_BREB.
        CREDIT_AUTHORIZED: ['FUNDING', 'SENT_TO_BREB', 'COMPENSATING'],
        FUNDING: ['FUNDED', 'COMPENSATING'],
        FUNDED: ['SENT_TO_BREB', 'COMPENSATING'],
        SENT_TO_BREB: ['COMPLETED', 'COMPENSATING', 'PENDING_CONFIRMATION'],
        // 24 h sin estado definitivo: no cambia (excepción STUCK_PENDING + human_task); el operador fuerza COMPLETED o COMPENSATING.
        PENDING_CONFIRMATION: ['COMPLETED', 'COMPENSATING'],
        // Si una compensación falla: reintentos + excepción, el estado se mantiene.
        COMPENSATING: ['FAILED'],
        COMPLETED: [],
        FAILED: [],
        CANCELLED: [],
        EXPIRED: [],
    },
});
export const PaymentIntentStatusSchema = PaymentIntentMachine.schema.meta({
    id: 'PaymentIntentStatus',
});
/** Tiempos de la saga (normativos). */
export const PAYMENT_INTENT_TIMINGS = {
    intentTtlSeconds: 10 * 60,
    scaChallengeTtlSeconds: 5 * 60,
    scaMaxAttempts: 3,
    brebConfirmationTimeoutSeconds: 20,
    /** Consultas de estado en PENDING_CONFIRMATION: 5 s, 15 s, 30 s, 1 m, 2 m, 5 m, luego cada 15 m. */
    pendingStatusQuerySeconds: [5, 15, 30, 60, 120, 300],
    pendingStatusQueryEverySeconds: 15 * 60,
    stuckPendingAfterSeconds: 24 * 60 * 60,
    /** Reintentos de posteo post-confirmación (capture/debitHeld): 1 m, 5 m, 15 m, 1 h → EXCEPTION. */
    postingRetrySeconds: [60, 300, 900, 3600],
    /** Reintento de paso UNKNOWN con la misma llave: 1 s, 2 s, 4 s… máx. 5. */
    stepRetryBackoffSeconds: [1, 2, 4, 8, 16],
    /** El worker retoma intenciones no terminales con `updated_at` más antiguo que esto. */
    resumeStaleAfterSeconds: 30,
};
/** Pasos de la saga (`payment_saga_steps.step`). */
export const SagaStepSchema = z
    .enum([
    'VALIDATE',
    'LIMITS',
    'AUTHORIZE',
    'FUND',
    'SEND',
    'AWAIT_CONFIRMATION',
    'CAPTURE',
    'DEBIT',
    'RELEASE',
    'REVERSE_FUNDING',
])
    .meta({ id: 'SagaStep' });
/** Estado de un intento de paso — §1.1. `UNKNOWN` (timeout/5xx) → reintento del mismo paso. */
export const SagaStepMachine = defineStateMachine({
    name: 'PaymentSagaStep',
    states: ['STARTED', 'SUCCEEDED', 'FAILED', 'UNKNOWN', 'COMPENSATED'],
    initial: ['STARTED'],
    terminal: ['FAILED', 'COMPENSATED'],
    transitions: {
        STARTED: ['SUCCEEDED', 'FAILED', 'UNKNOWN'],
        SUCCEEDED: ['COMPENSATED'],
        FAILED: [],
        // Un intento UNKNOWN se resuelve con un nuevo intento (attempt + 1) o con la consulta de estado.
        UNKNOWN: ['SUCCEEDED', 'FAILED'],
        COMPENSATED: [],
    },
});
export const SagaStepStatusSchema = SagaStepMachine.schema.meta({ id: 'SagaStepStatus' });
/** Estado contable post-confirmación (nunca se compensa hacia atrás). */
export const PostingStatusSchema = z
    .enum(['NOT_APPLICABLE', 'POSTED', 'RETRYING', 'EXCEPTION'])
    .meta({ id: 'PostingStatus' });
/** `Dispute` — §5. */
export const DisputeMachine = defineStateMachine({
    name: 'Dispute',
    states: ['OPEN', 'UNDER_REVIEW', 'ACCEPTED_FULL', 'ACCEPTED_PARTIAL', 'REJECTED', 'CLOSED'],
    initial: ['OPEN'],
    terminal: ['CLOSED'],
    invalidTransitionCode: 'PZ-DSP-409',
    transitions: {
        OPEN: ['UNDER_REVIEW', 'CLOSED'],
        UNDER_REVIEW: ['ACCEPTED_FULL', 'ACCEPTED_PARTIAL', 'REJECTED'],
        ACCEPTED_FULL: ['CLOSED'],
        ACCEPTED_PARTIAL: ['CLOSED'],
        REJECTED: ['CLOSED'],
        CLOSED: [],
    },
});
export const DisputeStatusSchema = DisputeMachine.schema.meta({ id: 'DisputeStatus' });
export const DisputeOutcomeSchema = z
    .enum(['ACCEPTED_FULL', 'ACCEPTED_PARTIAL', 'REJECTED'])
    .meta({ id: 'DisputeOutcome' });
export const DecisionChannelSchema = z.enum(['CONSOLE', 'API']);
/** `IncomingRefund` (devolución entrante del comercio) — §6. */
export const IncomingRefundMachine = defineStateMachine({
    name: 'IncomingRefund',
    states: ['RECEIVED', 'MATCHED', 'UNMATCHED', 'APPLIED', 'FAILED', 'RETURNED_TO_ACCOUNT'],
    initial: ['RECEIVED'],
    terminal: ['APPLIED', 'FAILED', 'RETURNED_TO_ACCOUNT'],
    transitions: {
        RECEIVED: ['MATCHED', 'UNMATCHED'],
        UNMATCHED: ['MATCHED', 'RETURNED_TO_ACCOUNT'],
        MATCHED: ['APPLIED', 'FAILED'],
        APPLIED: [],
        FAILED: [],
        RETURNED_TO_ACCOUNT: [],
    },
});
export const IncomingRefundStatusSchema = IncomingRefundMachine.schema.meta({
    id: 'IncomingRefundStatus',
});
export const RefundMatchMethodSchema = z.enum(['REFERENCE', 'MANUAL']);
export const UNMATCHED_REFUND_EXCEPTION_AFTER_SECONDS = 24 * 60 * 60;
/** Conciliación (M6). */
export const ReconciliationExceptionTypeSchema = z
    .enum([
    'MISSING_IN_BREB',
    'MISSING_IN_CREDIT_CORE',
    'MISSING_IN_DEPOSIT_CORE',
    'AMOUNT_MISMATCH',
    'ORPHAN_INCOMING_REFUND',
    'STUCK_PENDING',
    'POST_CONFIRMATION_POSTING_FAILED',
    'STATUS_CONFLICT',
    'COMPENSATION_FAILED',
])
    .meta({ id: 'ReconciliationExceptionType' });
export const ExceptionSeveritySchema = z
    .enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'])
    .meta({ id: 'ExceptionSeverity' });
export const ReconciliationExceptionStatusSchema = z.enum(['OPEN', 'RESOLVED']);
export const ReconciliationRunStatusSchema = z.enum(['RUNNING', 'COMPLETED', 'FAILED']);
export const PaymentMethodLinkStatusSchema = z.enum(['ACTIVE', 'SUSPENDED']);
//# sourceMappingURL=states.js.map